/**
 * The title logo's letters (08 §1), after the arcade's: fat, round "bubble" letters, each a few thick
 * strokes. A glyph is the strokes' centre lines in a box one unit tall (baseline y = 0, cap y = 1);
 * the surface is every point within `LOGO_STROKE` of a centre line, squashed a little front to back,
 * so the letters are rounded tubes blended where they meet (logo.ts).
 */

/** A centre line: points in glyph units. */
export type Stroke = ReadonlyArray<readonly [number, number]>;

export interface LogoGlyph {
  /** Width of the centre lines, glyph units (the letter is `2 * LOGO_STROKE` wider). */
  width: number;
  strokes: readonly Stroke[];
}

/** Stroke radius, glyph units: a fat letter, with just room for the counters of B and O. */
export const LOGO_STROKE = 0.17;

/** Points along an elliptical arc about (cx, cy), from angle a0 to a1 (radians), `steps` segments. */
function arc(
  cx: number, cy: number, rx: number, ry: number, a0: number, a1: number, steps: number,
): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let i = 0; i <= steps; i++) {
    const a = a0 + ((a1 - a0) * i) / steps;
    out.push([cx + Math.cos(a) * rx, cy + Math.sin(a) * ry]);
  }
  return out;
}

const HALF = Math.PI / 2;

/**
 * Where a stroke meets another part way along, it ends a little short of that one's centre line
 * (`INSET`): ending exactly on it would leave the two surfaces level there, and the blend would swell
 * them into a bump on the far side.
 */
const INSET = 0.12;

/** The upper bowl sits on the lower one, which is a little wider, as the arcade's B. */
const B: LogoGlyph = {
  width: 0.62,
  strokes: [
    [[0, 0], [0, 1]],
    [[INSET, 1], [0.28, 1], ...arc(0.28, 0.755, 0.29, 0.245, HALF, -HALF, 14), [INSET, 0.51]],
    [[0.28, 0.51], [0.33, 0.51], ...arc(0.33, 0.255, 0.29, 0.255, HALF, -HALF, 14), [INSET, 0]],
  ],
};

const U: LogoGlyph = {
  width: 0.64,
  strokes: [[[0, 1], ...arc(0.32, 0.32, 0.32, 0.32, Math.PI, 2 * Math.PI, 16), [0.64, 1]]],
};

const L: LogoGlyph = { width: 0.5, strokes: [[[0, 1], [0, 0], [0.5, 0]]] };

const E: LogoGlyph = {
  width: 0.5,
  strokes: [[[0.5, 1], [0, 1], [0, 0], [0.5, 0]], [[INSET, 0.5], [0.4, 0.5]]],
};

const O: LogoGlyph = { width: 0.68, strokes: [arc(0.34, 0.5, 0.34, 0.5, 0, 2 * Math.PI, 40)] };

const GLYPHS: Readonly<Record<string, LogoGlyph>> = { B, U, L, E, O };

/** The logo glyph for `ch`; throws for a letter the logo does not have. */
export function logoGlyph(ch: string): LogoGlyph {
  const g = GLYPHS[ch];
  if (!g) throw new Error(`logo: no glyph "${ch}"`);
  return g;
}

/** Distance in the glyph plane from (x, y) to a stroke's centre line; its nearest point goes in `out`. */
export function strokeClosest(s: Stroke, x: number, y: number, out: [number, number]): number {
  let best = Infinity;
  for (let i = 0; i + 1 < s.length; i++) {
    const a = s[i];
    const b = s[i + 1];
    if (!a || !b) continue;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len2 = dx * dx + dy * dy;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - a[0]) * dx + (y - a[1]) * dy) / len2)) : 0;
    const qx = a[0] + dx * t;
    const qy = a[1] + dy * t;
    const d2 = (x - qx) * (x - qx) + (y - qy) * (y - qy);
    if (d2 < best) {
      best = d2;
      out[0] = qx;
      out[1] = qy;
    }
  }
  return Math.sqrt(best);
}
